import React from 'react';
import update from 'immutability-helper';

import storage from '@dashboard/services/storage';
import sessionTracker from '@dashboard/services/sessionTracker';

import { login, verifyAuth, getAdminSeller } from './helpers';
import { get } from 'lodash';
import { tracking } from '@dashboard/lib/tracking';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { AdminRolesEnum } from '@/dashboard/enums/admin-roles';

const AuthContext = React.createContext();

class AuthProvider extends React.Component {
  state = {
    isAuthenticating: true,
    userId: storage.local.getItem('userId'),
    sellerId: storage.local.getItem('sellerId'),
    userSellerId: storage.local.getItem('userSellerId'),
    isAuth: !!storage.local.getItem('auth_token'),
    user: {},
    seller: {},
  };

  componentDidMount() {
    this.verifyAuth();

    const { user } = this.state;

    const token = this.getToken();

    sessionTracker(user && user._id, token);
  }

  componentDidUpdate(prevProps, prevState) {
    const { isAuth, user, seller } = this.state;

    if (!prevState.isAuth && isAuth) {
      this.verifyAuth();
    }

    if (!prevState.user._id && user._id) {
      const token = this.getToken();

      sessionTracker(user && user._id, token);
    }

    if (user) {
      tracking.identifyUser(user._id);
      tracking.updateUser({
        $email: user.email,
        $name: user.name,
        $created: user.createdAt,
        $seller: seller?._id,
      });
    }
  }

  verifyAuth = async () => {
    const { isAuth } = this.state;
    const token = this.getToken();

    if (isAuth && token) {
      const response = await verifyAuth(token);

      if (response.data) {
        const { seller, ...user } = response.data;

        this.setLogged({ user, seller });
      } else {
        this.logout();
      }
    }
  };

  setLogged = ({ user, seller, token }) => {
    storage.local.setItem('userId', user._id);
    storage.local.setItem('sellerId', seller?._id);
    storage.local.setItem('userSellerId', user.seller?._id);
    token && this.setToken(token);

    this.setState({
      isAuthenticating: false,
      isAuth: true,
      userId: user._id,
      userSellerId: seller?._id,
      sellerId: seller?._id,
      user,
      seller: seller || {},
    });

    tracking.identifyUser(user._id);
  };

  login = data =>
    login(data)
      .then(
        response =>
          new Promise(resolve => {
            if (response.auth) {
              storage.local.setItem('userId', response.data.user._id);
              storage.local.setItem('sellerId', response.data.user.seller?._id);
              this.setToken(response.data.token);

              this.setState({
                isAuthenticating: false,
                isAuth: true,
                userId: response.data.user._id,
                sellerId: response.data.user.seller?._id,
                seller: response.data.user.seller || {},
                user: response.data.user,
              });

              tracking.identifyUser(response.data.user._id);

              resolve({
                error: '',
                isAuth: true,
                data: response.data,
              });
            }

            throw new Error(response.message);
          }),
      )
      .catch(err => {
        return {
          error: err.message,
          isAuth: false,
        };
      });

  logout = () => {
    storage.local.clear('userId');
    storage.local.clear('sellerId');
    storage.local.clear('auth_token');

    tracking.logout();

    this.setState(
      {
        isAuthenticating: false,
        isAuth: false,
        userId: '',
        sellerId: '',
      },
      () => (window.location.href = '/'),
    );
  };

  logAdminInAsSeller = async sellerId => {
    const token = this.getToken();

    const response = await getAdminSeller(sellerId, token);

    if (response.data) {
      this.setState({ sellerId: response.data._id });
      this.setState({
        user: this.state.user,
        seller: response.data,
      });
    } else {
      window.alert('Unable to fetch seller, check console');
    }
  };

  setToken = token => {
    storage.local.setItem('auth_token', token);
  };

  getToken = () => storage.local.getItem('auth_token');

  setUser = user => {
    const newState = update(this.state, {
      user: {
        $set: user,
      },
    });

    this.setState(newState);
  };

  setSeller = seller => {
    const newState = update(this.state, {
      seller: {
        $set: seller,
      },
    });

    this.setState(newState);
  };

  mergeSeller = seller => {
    const newState = update(this.state, {
      seller: {
        $merge: seller,
      },
    });

    this.setState(newState);
  };

  render() {
    const { isAuth, userId, sellerId, userSellerId, user, seller, isAuthenticating } = this.state;

    const isAdmin = isAuth && user && Object.values(AdminRolesEnum).includes(user.role);
    const plan = get(seller, 'plan') || {};
    const isTrialing = seller?.subscriptionStatus === 'trialing';
    const isTrialExpired = seller?.subscriptionStatus === 'trial_expired';
    const isCanceled = seller?.subscriptionStatus === 'canceled';
    const isCanceling = !!seller?.stripeSubscriptionCancelAt;
    const hasActiveSubscription =
      seller?.subscriptionStatus === 'active' || seller?.subscriptionStatus === 'legacy';
    const isConnectedToStripe = seller?._id ? checkIsConnectedToStripe(seller) : true; // only check if seller exists
    const hasPastDueSubscription = seller?.stripeSubscriptionStatus === 'past_due';

    const userRoleInSeller = seller?.users?.find(u => u.user === userId)?.role;
    const isOwner = userRoleInSeller === 'owner' || isAdmin;
    const isAdminLoggedInAsSeller = isAdmin && sellerId !== userSellerId;

    return (
      <AuthContext.Provider
        value={{
          verifyAuth: this.verifyAuth,
          isAuth,
          userId,
          sellerId,
          userSellerId,
          plan,
          hasActiveSubscription,
          hasPastDueSubscription,
          isConnectedToStripe,
          login: this.login,
          logout: this.logout,
          setToken: this.setToken,
          token: this.getToken(),
          user,
          userRoleInSeller,
          isOwner,
          seller,
          setLogged: this.setLogged,
          setUser: this.setUser,
          setSeller: this.setSeller,
          mergeSeller: this.mergeSeller,
          isAuthenticating,
          isAdmin,
          logAdminInAsSeller: this.logAdminInAsSeller,
          isAdminLoggedInAsSeller,
          isTrialing,
          isTrialExpired,
          isCanceled,
          isCanceling,
        }}
      >
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}

const AuthConsumer = AuthContext.Consumer;

function useAuthState() {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuthState must be used within a AuthProvider');
  }

  return context;
}

function useAuth() {
  return useAuthState();
}

export { AuthProvider, AuthConsumer, AuthContext, useAuth };
